import { pickLegalLocale } from "@/lib/legal/legalI18n";
import type { Language } from "@/hooks/useLanguage";
import type { SeoLocale, SeoPageKey } from "./seoTypes";
import { isIndexedPageKey } from "./seoContent";
import { GUIDE_PAGE_KEYS, matchPublicPath, PUBLIC_SEGMENTS } from "@/lib/site/publicPages";

const LANGS = new Set<string>(["cs", "en"]);

/** First path segment after `/:lang/` for product routes. */
export const APP_FIRST_SEGMENTS: ReadonlySet<string> = new Set<string>([
  "dashboard",
  "analytics",
  "auth",
  "reset-password",
  "profile",
  "settings",
  "feedback",
  "audit-logs",
  "data-export",
  "clients",
  "invoices",
  "receipts",
  "transactions",
  "webfaktura",
  "templates",
  "business",
  "personal",
  "icons",
  "admin",
  "invite",
  "join",
  "design-lab",
  "unsubscribe",
  "missing-documents",
]);

const PUBLIC_TO_KEY: Record<string, SeoPageKey> = {
  privacy: "privacy",
  dpa: "dpa",
  terms: "terms",
  cookies: "cookies",
  contact: "contact",
  changelog: "changelog",
};

const GUIDE_TO_KEY = new Map<string, SeoPageKey>(
  GUIDE_PAGE_KEYS.map((key) => [PUBLIC_SEGMENTS[key], key])
);

function isSeoLocale(s: string): s is SeoLocale {
  return LANGS.has(s);
}

function buildCanonicalPath(pathname: string): string {
  const noQuery = pathname.split("?")[0] || "/";
  const trimmed = noQuery.replace(/\/+$/, "");
  return trimmed.length > 0 ? trimmed : "/";
}

function classifyPageKey(segments: string[]): SeoPageKey {
  if (segments.length === 0) {
    return "landing";
  }

  const head = segments[0];
  if (segments.length === 1) {
    const publicKey = PUBLIC_TO_KEY[head] ?? GUIDE_TO_KEY.get(head);
    if (publicKey) {
      return publicKey;
    }
    if (head === "auth") {
      return "auth";
    }
    if (head === "reset-password") {
      return "resetPassword";
    }
  }

  if (APP_FIRST_SEGMENTS.has(head)) {
    return "app";
  }

  return "notFound";
}

export interface ResolvedSeoRoute {
  lang: SeoLocale;
  pageKey: SeoPageKey;
  /** Normalized path of the URL itself (canonicals of indexed pages come from `indexedPathFor`). */
  canonicalPath: string;
  indexable: boolean;
}

/**
 * Maps URL to SEO page key, display language, canonical path, and indexability.
 */
export function resolveSeoRoute(pathname: string, fallbackLanguage: Language): ResolvedSeoRoute {
  const clean = pathname.split("?")[0] || "/";
  const parts = clean.split("/").filter(Boolean);

  // Czech pages at the root (zettl.cz): `/`, `/kontakt` …
  const rootPage = matchPublicPath(clean);
  if (rootPage?.layout === "root") {
    return {
      lang: "cs",
      pageKey: rootPage.key,
      canonicalPath: buildCanonicalPath(clean),
      indexable: true,
    };
  }

  const first = parts[0];

  if (!isSeoLocale(first)) {
    const lang = pickLegalLocale(fallbackLanguage) as SeoLocale;
    return {
      lang,
      pageKey: "notFound",
      canonicalPath: buildCanonicalPath(clean),
      indexable: false,
    };
  }

  const lang = first;
  const segments = parts.slice(1);
  const pageKey = classifyPageKey(segments);
  const canonicalPath = buildCanonicalPath(clean);
  const indexable = isIndexedPageKey(pageKey);

  return {
    lang,
    pageKey,
    canonicalPath,
    indexable,
  };
}
